import type { AudienceMode } from "../lib/audienceMode";
import { type DeploymentStackConfig, stackHasRds } from "../lib/deploymentStack";
import { fmtInt } from "./costCopy";
import type { Locale } from "./locale";
import type { Workload } from "../lib/types";

const DAYS = 30;

export type LoadSnapshot = {
  dailyRequests: number;
  monthlyRequests: number;
  avgRps: number;
  peakRps: number;
  lines: string[];
};

export function buildLoadSnapshot(
  locale: Locale,
  w: Workload,
  audience: AudienceMode = "technical",
): LoadSnapshot {
  const dailyRequests = w.usersDaily * w.requestsPerUser;
  const monthlyRequests = dailyRequests * DAYS;
  const avgRps = dailyRequests / 86_400;
  const peakRps = Math.max(avgRps * 3, avgRps + 0.01);
  const aiCalls = Math.round(monthlyRequests * w.aiRequestShare);
  const simple = audience === "simple";

  if (locale === "uk") {
    return {
      dailyRequests,
      monthlyRequests,
      avgRps,
      peakRps,
      lines: simple
        ? [
            `Користувачі разом роблять приблизно ${fmtInt(dailyRequests, locale)} дій на день — це порядку ${fmtInt(monthlyRequests, locale)} на місяць.`,
            "Навантаження протягом доби буває нерівним: у «гарячі» години система працює інтенсивніше, ніж у середньому.",
            `Орієнтовно ${fmtInt(aiCalls, locale)} разів на місяць можуть залучати відповіді AI (${Math.round(w.aiRequestShare * 100)}% звернень) — залежить від вашого сценарію.`,
          ]
        : [
            `Запитів на день: ~${fmtInt(dailyRequests, locale)} (~${fmtInt(monthlyRequests, locale)} / міс).`,
            `Середній RPS: ~${avgRps.toFixed(2)}, орієнтир піку ~${peakRps.toFixed(2)} (×3 до середнього).`,
            `AI-викликів / міс (орієнтир): ~${fmtInt(aiCalls, locale)} при частці ${Math.round(w.aiRequestShare * 100)}%.`,
          ],
    };
  }

  if (locale === "es") {
    return {
      dailyRequests,
      monthlyRequests,
      avgRps,
      peakRps,
      lines: simple
        ? [
            `Unas ${fmtInt(dailyRequests, locale)} interacciones al día en total — del orden de ${fmtInt(monthlyRequests, locale)} al mes.`,
            "El uso no es uniforme: hay horas punta con más carga que el promedio.",
            `Unos ${fmtInt(aiCalls, locale)} usos/mes pueden implicar IA (${Math.round(w.aiRequestShare * 100)}% del tráfico), según tu escenario.`,
          ]
        : [
            `Peticiones/día: ~${fmtInt(dailyRequests, locale)} (~${fmtInt(monthlyRequests, locale)} / mes).`,
            `RPS medio: ~${avgRps.toFixed(2)}, pico orientativo ~${peakRps.toFixed(2)} (×3 la media).`,
            `Llamadas IA/mes (orientativo): ~${fmtInt(aiCalls, locale)} con ${Math.round(w.aiRequestShare * 100)}% de cuota.`,
          ],
    };
  }

  return {
    dailyRequests,
    monthlyRequests,
    avgRps,
    peakRps,
    lines: simple
      ? [
          `Roughly ${fmtInt(dailyRequests, locale)} user actions per day — on the order of ${fmtInt(monthlyRequests, locale)} per month.`,
          "Traffic is uneven through the day; peak periods are hotter than the daily average.",
          `About ${fmtInt(aiCalls, locale)} calls per month may use AI (${Math.round(w.aiRequestShare * 100)}% of traffic), depending on your scenario.`,
        ]
      : [
          `Requests/day: ~${fmtInt(dailyRequests, locale)} (~${fmtInt(monthlyRequests, locale)} / mo).`,
          `Average RPS: ~${avgRps.toFixed(2)}, rough peak ~${peakRps.toFixed(2)} (×3 average).`,
          `AI calls/mo (indicative): ~${fmtInt(aiCalls, locale)} at ${Math.round(w.aiRequestShare * 100)}% share.`,
        ],
  };
}

export function buildBottleneckHints(
  locale: Locale,
  w: Workload,
  stack: DeploymentStackConfig,
  audience: AudienceMode = "technical",
): string[] {
  const out: string[] = [];
  const dailyReq = w.usersDaily * w.requestsPerUser;
  const peakRps = Math.max((dailyReq / 86_400) * 3, 0.05);
  const simple = audience === "simple";

  if (locale === "uk") {
    if (stack.networkExposure === "private") {
      out.push(
        simple
          ? "Приватна схема: важливо, щоб дані не «витікали» обхідними шляхами — це перевіряє команда безпеки."
          : "Приватна інфра: перевір відсутність випадкових публічних IP/ALB і зайвого egress — ендпоінти та трафік не повинні «текти» в обхід політик.",
      );
      out.push(
        simple
          ? "Доступ до зовнішніх сервісів (включно з AI) має бути обмежений списком дозволених адрес."
          : "HTTP/DNS-клієнти в Lambda чи EC2: allowlist хостів, мінімальні IAM-права, секрети лише з Secrets Manager; аудит маршрутів і VPC Flow Logs.",
      );
    }
    if (stack.networkExposure === "public" && !stack.albWafEnabled && peakRps > 25) {
      out.push(
        simple
          ? "Публічний застосунок із великим потоком запитів без додаткового захисту — варто обговорити з командою фільтрацію зловмисного трафіку."
          : "Публічний API з помітним RPS без WAF у стеку — розгляни AWS WAF або жорсткіші rate limits / usage plans на API Gateway.",
      );
    }
    if (stack.backend === "lambda" && peakRps > 80) {
      out.push(
        simple
          ? "Дуже висока кількість одночасних запитів може вимагати окремих лімітів і резерву потужності — питання до інженерів."
          : "Високий пік RPS на Lambda — перевір concurrency limits, provisioned concurrency, cold start budget.",
      );
    }
    if (stack.database === "dynamo" && w.enabledFeatures.includes("search")) {
      out.push(
        simple
          ? "Пошук по великій базі без окремого пошукового рішення часто стає вузьким місцем."
          : "Пошук на DynamoDB — вузьке місце в GSI / hot partitions; розглянь OpenSearch або кеш.",
      );
    }
    if (stackHasRds(stack) && w.aiRequestShare > 0.25) {
      out.push(
        simple
          ? "Поєднання AI та класичної бази даних потребує контролю кількості одночасних з'єднань."
          : "Багато AI + RDS — пул з'єднань і час транзакцій; RDS Proxy або обмеження паралелізму з Lambda.",
      );
    }
    if (stack.frontend === "ec2_spa" && dailyReq > 500_000) {
      out.push(
        simple
          ? "Один сервер для всього фронту при дуже великому трафіку зазвичай замінюють на розподілену схему."
          : "Великий трафік при SPA на одному EC2 — краще CloudFront + S3 або горизонтальний scale за ALB.",
      );
    }
    if (stack.backend === "eks" && w.enabledFeatures.includes("realtime")) {
      out.push(
        simple
          ? "Онлайн-режим на кластері Kubernetes потребує окремого плану мережі та стабільних з'єднань."
          : "EKS + realtime — окремо плануй WebSocket stickiness, ingress controller і мережеві політики.",
      );
    }
    if (stack.frontend === "cloudfront" && w.enabledFeatures.includes("files")) {
      out.push(
        simple
          ? "Великі файли через CDN варто кешувати й віддавати захищеними посиланнями."
          : "CloudFront + великі файли — cache TTL, signed URLs, optional S3 Transfer Acceleration.",
      );
    }
    if (out.length === 0) {
      out.push(
        simple
          ? "За введеними даними грубих «вузьких місць» у шаблоні не видно — уточніть піки з командою."
          : "За поточними параметрами критичних «вузьких місць» у шаблоні немає — перевір піки та ліміти провайдерів LLM вручну.",
      );
    }
    return out;
  }

  if (locale === "es") {
    if (stack.networkExposure === "private") {
      out.push(
        simple
          ? "Arquitectura privada: evita fugas de datos por rutas no controladas."
          : "Private stack: watch for stray public IPs/ALBs and broad egress — endpoints and data should not leak around your policies.",
      );
      out.push(
        simple
          ? "Limita qué hosts externos (incl. IA) puede llamar tu aplicación."
          : "HTTP/DNS clients in Lambda or EC2: host allowlists, least-privilege IAM, secrets only from Secrets Manager; audit routes and VPC Flow Logs.",
      );
    }
    if (stack.networkExposure === "public" && !stack.albWafEnabled && peakRps > 25) {
      out.push(
        simple
          ? "Mucho tráfico público sin capa extra de protección: hablar de WAF o límites con el equipo."
          : "Public API with meaningful RPS and no WAF in stack — consider AWS WAF or stricter rate limits / usage plans on API Gateway.",
      );
    }
    if (stack.backend === "lambda" && peakRps > 80) {
      out.push(
        simple
          ? "Picos muy altos en funciones serverless: revisar límites y capacidad reservada con ingeniería."
          : "High peak RPS on Lambda — check concurrency limits, provisioned concurrency, cold-start budget.",
      );
    }
    if (stack.database === "dynamo" && w.enabledFeatures.includes("search")) {
      out.push(
        simple
          ? "Búsqueda fuerte sobre una sola tabla suele necesitar índice o caché aparte."
          : "Search on DynamoDB — watch GSI / hot partitions; consider OpenSearch or caching.",
      );
    }
    if (stackHasRds(stack) && w.aiRequestShare > 0.25) {
      out.push(
        simple
          ? "Mucha IA + base relacional: vigilar conexiones simultáneas."
          : "Heavy AI + RDS — connection pools and transaction time; RDS Proxy or cap Lambda parallelism.",
      );
    }
    if (stack.frontend === "ec2_spa" && dailyReq > 500_000) {
      out.push(
        simple
          ? "Mucho tráfico con un solo EC2 para el SPA: suele migrarse a CDN + S3 o balanceador."
          : "High traffic with SPA on one EC2 — prefer CloudFront + S3 or scale behind an ALB.",
      );
    }
    if (stack.backend === "eks" && w.enabledFeatures.includes("realtime")) {
      out.push(
        simple
          ? "Tiempo real en Kubernetes requiere plan de red y sesiones aparte."
          : "EKS + realtime — plan WebSocket stickiness, ingress, and network policies separately.",
      );
    }
    if (stack.frontend === "cloudfront" && w.enabledFeatures.includes("files")) {
      out.push(
        simple
          ? "Archivos grandes: caché en CDN y URLs firmadas."
          : "CloudFront + large files — cache TTL, signed URLs, optional S3 Transfer Acceleration.",
      );
    }
    if (out.length === 0) {
      out.push(
        simple
          ? "Sin cuellos de botella obvios en la plantilla — validar picos con el equipo."
          : "No critical template bottlenecks for current inputs — still validate peaks and LLM provider limits.",
      );
    }
    return out;
  }

  if (stack.networkExposure === "private") {
    out.push(
      simple
        ? "Private setup: make sure data does not leak through accidental public paths — your security team checks this."
        : "Private stack: watch for stray public IPs/ALBs and broad egress — endpoints and data should not leak around your policies.",
    );
    out.push(
      simple
        ? "External calls (including AI) should use a strict allowlist of destinations."
        : "HTTP/DNS clients in Lambda or EC2: host allowlists, least-privilege IAM, secrets only from Secrets Manager; audit routes and VPC Flow Logs.",
    );
  }
  if (stack.networkExposure === "public" && !stack.albWafEnabled && peakRps > 25) {
    out.push(
      simple
        ? "A busy public app without an extra shield layer — discuss filtering abusive traffic with your team."
        : "Public API with meaningful RPS and no WAF in stack — consider AWS WAF or stricter rate limits / usage plans on API Gateway.",
    );
  }
  if (stack.backend === "lambda" && peakRps > 80) {
    out.push(
      simple
        ? "Very high simultaneous requests may need reserved headroom and limits — an engineering discussion."
        : "High peak RPS on Lambda — check concurrency limits, provisioned concurrency, cold-start budget.",
    );
  }
  if (stack.database === "dynamo" && w.enabledFeatures.includes("search")) {
    out.push(
      simple
        ? "Heavy search on one database table often becomes the bottleneck without a dedicated search layer."
        : "Search on DynamoDB — watch GSI / hot partitions; consider OpenSearch or caching.",
    );
  }
  if (stackHasRds(stack) && w.aiRequestShare > 0.25) {
    out.push(
      simple
        ? "Lots of AI plus a classic database needs control of concurrent connections."
        : "Heavy AI + RDS — connection pools and transaction time; RDS Proxy or cap Lambda parallelism.",
    );
  }
  if (stack.frontend === "ec2_spa" && dailyReq > 500_000) {
    out.push(
      simple
        ? "One server for the whole frontend at very high traffic is usually replaced with a distributed edge pattern."
        : "High traffic with SPA on one EC2 — prefer CloudFront + S3 or scale behind an ALB.",
    );
  }
  if (stack.backend === "eks" && w.enabledFeatures.includes("realtime")) {
    out.push(
      simple
        ? "Live features on Kubernetes need a separate network and session plan."
        : "EKS + realtime — plan WebSocket stickiness, ingress, and network policies separately.",
    );
  }
  if (stack.frontend === "cloudfront" && w.enabledFeatures.includes("files")) {
    out.push(
      simple
        ? "Large files through a CDN should be cached and served with time-limited links."
        : "CloudFront + large files — cache TTL, signed URLs, optional S3 Transfer Acceleration.",
    );
  }
  if (out.length === 0) {
    out.push(
      simple
        ? "No obvious template bottlenecks for these inputs — still validate peaks with your team."
        : "No critical template bottlenecks for current inputs — still validate peaks and LLM provider limits.",
    );
  }
  return out;
}
